import React, { useState } from "react";
import { useSearchParams } from 'react-router-dom';
import {
  Share2,
  LogIn,
  PlusSquare,
  LogOut,
  Sun,
  Moon,
  SunMoon,
  LanguagesIcon,
  ClipboardList,
  ArrowRight,
  Link
} from "lucide-react"; // Import icons
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAppStore } from "@/hooks/useAppStore";
import { SettingsDialog } from "../../dialogs/SettingsDialog";
import { ShareRoomDialog } from "../../dialogs/ShareRoomDialog";
import { useTheme } from "../../themes/ThemeProvider";
import { useTranslation } from "@/lib/translate";
import { LanguageSettingsDialog } from "../../dialogs/LanguageSettingsDialog";
import { ConversationContextDialog } from "../../dialogs/ConversationContextDialog";
import { Input } from "@/components/ui/input";
import { Settings } from "lucide-react";

interface AdminHeaderProps {
  showSettings: boolean;
  onToggleSettings: () => void;
}

export const AdminHeader: React.FC<AdminHeaderProps> = ({ showSettings, onToggleSettings }) => {
  const { setTheme, resolvedTheme } = useTheme();
  const [searchParams] = useSearchParams();
  const routeRoomId = searchParams.get('r') // Get room ID from URL

  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const adminUser = useAppStore(state => state.adminUser);
  const adminLogout = useAppStore(state => state.adminLogout);
  const [menuOpen, setMenuOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [languageSettingsDialogOpen, setLanguageSettingsDialogOpen] = useState(false);
  const [conversationContextOpen, setConversationContextOpen] = useState(false);
  const [roomId, setRoomId] = useState(routeRoomId);
  const t = useTranslation(preferredLanguage);

  const handleCreateRoom = async () => {
    // Optional confirmation
    const proceed = window.confirm(t("This will create a new chat room in a new window/tab, continue?"))
    if (proceed) {
      window.open(`/new-room?r=${routeRoomId}`, "_blank");
    }
  };

  const handleLogout = () => {
    adminLogout();
    window.location.href = "/admin-login"
  };

  const clientConfig = useAppStore((state) => state.clientConfig);

  const logoSrc = resolvedTheme === 'dark' && clientConfig?.config?.logoDark
    ? clientConfig.config.logoDark
    : clientConfig?.config?.logo;

  function searchRoom(event: React.SyntheticEvent): void {
    event.preventDefault();
    window.location.href = `/admin?r=${roomId}`;
  }

  return (
    <>
      <header className="w-full h-[50px] flex flex-shrink-0 justify-between items-center border-b border-neutral-500/30 bg-chotto-header/90 px-4 py-4">
        <div className="hidden md:flex items-center space-x-3 w-1/3">
          <img src={logoSrc} width={clientConfig?.config?.logoWidth || 150} />
          {routeRoomId && (
            <Button
              variant="link"
              size="xs" // Custom size or adjust padding
              className="inline-flex items-center gap-1 rounded-full bg-chotto-primary px-4 py-0.5 text-xs uppercase text-chotto-font-primary transition-all hover:brightness-110 hover:no-underline"
              onClick={() => setShareDialogOpen(true)}
            >
              <span className="hidden min-[510px]:inline">{t("Share")} </span>
              <span className="hidden max-[410px]:inline">{routeRoomId}</span>
              <Share2 size={4} style={{ height: '14px' }} />
            </Button>
          )}
        </div>
        <div className="flex justify-center items-center w-2/3 md:w-1/3">
          <div className="text-sm">{t("Room #")}:</div>
          <form onSubmit={searchRoom}>
            <div className="flex items-center space-x-3 w-full ml-2">
              <Input
                name="roomId"
                autoComplete="on"
                type="text"
                placeholder={t("Enter Room Id...")}
                value={roomId || ""}
                onChange={(e) => setRoomId(e.target.value)}
                className="flex-1 border-1 focus:border-chotto-mic-green dark:focus:border-chotto-mic-green rounded-lg px-4 py-2 text-sm font-sans"
              />
              <Button
                onClick={searchRoom}
                className="h-7 w-7 rounded-full bg-chotto-mic-green disabled:bg-neutral-300 dark:disabled:bg-neutral-700 p-0 flex items-center justify-center"
              >
                <ArrowRight className="h-5 w-5 text-white" />
              </Button>
            </div>
          </form>
        </div>
        <div className="flex justify-end items-center space-x-4 w-1/3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleSettings}
            className="rounded hover:bg-muted transition-colors"
            title={showSettings ? t("Hide Settings") : t("Show Settings")}
          >
            <Settings size={20} />
          </Button>
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <div
                style={{ perspective: "100px" }}
                className="w-9 h-6 flex flex-col justify-between cursor-pointer transition-all duration-300">
                <span
                  className={`h-1 w-full bg-chotto-primary rounded-full transform transition duration-300 
              ${menuOpen ? "rotate-45 translate-y-[5px] md:translate-y-[10px]" : ""}`}
                ></span>
                <span
                  className={`h-1 w-full bg-chotto-primary rounded-full transition-all duration-300 
              ${menuOpen ? "[transform:translateZ(-30px)] opacity-60" : "translate-z-0 opacity-100"}`}
                ></span>
                <span
                  className={`h-1 w-full bg-chotto-primary rounded-full transform transition duration-300 
              ${menuOpen ? "-rotate-45 -translate-y-[5px] md:-translate-y-[10px]" : ""}`}
                ></span>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem className="cursor-pointer">
                <div className="text-sm flex flex-col break-all space-y-2 w-full">
                  <div className="flex justify-center">{adminUser?.firstName} {adminUser?.lastName}</div>
                  <div className="flex justify-center">
                    <img width={30} height={30} src={adminUser?.avatarURL}></img>
                  </div>
                  <div className="flex justify-center">{adminUser?.email}</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-neutral-700" />
              <DropdownMenuItem
                onSelect={handleCreateRoom}
                className="cursor-pointer "
              >
                <PlusSquare className="mr-2 h-4 w-4" />
                <span>{t("Create A Room")}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-neutral-700" />
              <DropdownMenuItem
                onSelect={() => setTheme("light")}
                className="cursor-pointer "
              >
                <Sun className="mr-2 h-4 w-4" />
                <span>{t("Light Mode")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setTheme("dark")}
                className="cursor-pointer "
              >
                <Moon className="mr-2 h-4 w-4" />
                <span>{t("Dark Mode")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setTheme("system")}
                className="cursor-pointer "
              >
                <SunMoon className="mr-2 h-4 w-4" />
                <span>{t("System Mode")}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-neutral-700" />
              <DropdownMenuItem
                onSelect={() => setLanguageSettingsDialogOpen(true)}
                className="cursor-pointer ">
                <LanguagesIcon className="mr-2 h-4 w-4" />
                <span>{t("Set Interface Language")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setConversationContextOpen(true)}
                className="cursor-pointer ">
                <ClipboardList className="mr-2 h-4 w-4" />
                <span>{t("Set Conversation Context")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => handleLogout()}
                className="cursor-pointer "
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>{t("Logout")}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-neutral-700" />
              <DropdownMenuItem
                onSelect={() => window.open("https://shisa.ai/", "_blank")}
                className="cursor-pointer "
              >
                {t("Powered by Shisa.ai")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Render Dialogs */}
      <ShareRoomDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
      />
      <SettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen} />
      <LanguageSettingsDialog
        open={languageSettingsDialogOpen}
        onOpenChange={setLanguageSettingsDialogOpen}
      />
      <ConversationContextDialog
        open={conversationContextOpen}
        onOpenChange={setConversationContextOpen}
      />
    </>
  );
};
